import { FC, ReactNode } from 'react';

interface ErrorFallbackProps {
  error: Error | null;
  title?: string;
  description?: string;
  supportEmail?: string;
  children?: ReactNode;
}

export const ErrorFallback: FC<ErrorFallbackProps> = ({
  error,
  title = 'Oops! Something went wrong',
  description = 'We apologize for the inconvenience. An error has occurred.',
  supportEmail,
  children,
}) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <svg
              className="h-6 w-6 text-red-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            {title}
          </h2>
          <p className="mt-2 text-sm text-gray-600">{description}</p>
          {error && (
            <div className="mt-4 p-4 bg-red-50 rounded-md w-full mx-auto text-center whitespace-normal">
              <p className="text-sm text-red-700 break-words">
                {error.message}
              </p>
            </div>
          )}
          {children && <div className="mt-6">{children}</div>}
          <div className="mt-6">
            <p className="text-sm text-gray-500">
              {supportEmail ? (
                <>
                  <p>
                    If this error persists, please contact our support team.
                  </p>
                  <p>
                    Support Email:{' '}
                    <a
                      href={`mailto:${supportEmail}`}
                      className="text-blue-500 hover:text-blue-600"
                    >
                      {supportEmail}
                    </a>
                  </p>
                </>
              ) : (
                <p>If this error persists, please contact our support team.</p>
              )}
            </p>
          </div>
          <div className="mt-6">
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorFallback;
